import { useCallback, useRef, useState } from 'react';
import {
  hasTrainingTranslationGaps,
  type TranslationTraining
} from './translationGaps';
import {
  isTrainingContentInSync,
  normalizeContentTimestamp
} from '../trainingContentTimestamps';
import type { EditorTraining } from '~/shell/Store';

type PublishTrainingParams = {
  should_auto_update_translations: boolean;
};

type PublishTrainingResponse = {
  redirectUrl?: string;
};

type CourseInfoResponse = {
  error?: boolean;
  training?: TranslationTraining;
};

type CourseEditorApi = {
  publishTraining: (
    trainingId: number,
    params: PublishTrainingParams
  ) => Promise<PublishTrainingResponse>;
  getCourseInfo: (trainingId: number) => Promise<CourseInfoResponse>;
};

type UseExitPublishFlowParams = {
  apiService: CourseEditorApi | null;
  training: TranslationTraining | null | undefined;
  publishRedirectUrl?: string | null;
  exitRedirectUrl: string;
  shouldCheckTranslationsBeforeExit: boolean;
  hasTranslationGaps: boolean;
  setTraining: (training: TranslationTraining) => void;
  markTrainingPublished: () => void;
  setDisplayUpdateTranslationsModal: (display: boolean) => void;
  setShouldAutoUpdateTranslations: (
    shouldAutoUpdateTranslations: boolean
  ) => void;
  onError: () => void;
};

type OnExitOptions = {
  /**
   * Assign reuses this flow but must still publish/redirect via publish URL
   * even when content is unchanged. Only the X/close path may skip publish.
   */
  requirePublish?: boolean;
};

type PublishOptions = {
  notifyOnFailure?: boolean;
  showBusyState?: boolean;
};

type UseExitPublishFlowResult = {
  isExitInProgress: boolean;
  publish: (
    shouldAutoUpdateTranslations: boolean,
    options?: PublishOptions
  ) => Promise<void>;
  onExit: (options?: OnExitOptions) => Promise<void>;
};

export const useExitPublishFlow = ({
  apiService,
  training,
  publishRedirectUrl,
  exitRedirectUrl,
  shouldCheckTranslationsBeforeExit,
  hasTranslationGaps,
  setTraining,
  markTrainingPublished,
  setDisplayUpdateTranslationsModal,
  setShouldAutoUpdateTranslations,
  onError
}: UseExitPublishFlowParams): UseExitPublishFlowResult => {
  const [isExitInProgress, setIsExitInProgress] = useState(false);
  const exitInProgressRef = useRef(false);

  const doPublish = useCallback(
    async (shouldAutoUpdateTranslations: boolean): Promise<boolean> => {
      if (!apiService || training?.id == null) return false;

      const publishResponse = await apiService.publishTraining(training.id, {
        should_auto_update_translations: shouldAutoUpdateTranslations
      });

      if (publishResponse && !(publishResponse as { error?: boolean }).error) {
        markTrainingPublished();
      }

      const _redirect = (url: string) => {
        if (window.top && window.self !== window.top) {
          window.top.location.href = url;
        } else {
          window.location.href = url;
        }
        return true;
      };

      if (publishRedirectUrl) {
        return _redirect(
          process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL + publishRedirectUrl
        );
      }

      if (publishResponse?.redirectUrl && window) {
        return _redirect(publishResponse.redirectUrl);
      }

      return false;
    },
    [apiService, markTrainingPublished, publishRedirectUrl, training]
  );

  const publish = useCallback(
    async (shouldAutoUpdateTranslations: boolean, options?: PublishOptions) => {
      if (exitInProgressRef.current) return;

      const notifyOnFailure = options?.notifyOnFailure ?? true;
      const showBusyState = options?.showBusyState ?? true;

      exitInProgressRef.current = true;
      if (showBusyState) setIsExitInProgress(true);

      let shouldResetExitInProgress = true;

      try {
        setShouldAutoUpdateTranslations(shouldAutoUpdateTranslations);

        const didNavigate = await doPublish(shouldAutoUpdateTranslations);
        if (!didNavigate) {
          if (notifyOnFailure) onError();
          return;
        }

        setDisplayUpdateTranslationsModal(false);
        shouldResetExitInProgress = false;
      } catch {
        if (notifyOnFailure) onError();
      } finally {
        if (shouldResetExitInProgress) {
          exitInProgressRef.current = false;
          setIsExitInProgress(false);
        }
      }
    },
    [
      doPublish,
      onError,
      setDisplayUpdateTranslationsModal,
      setShouldAutoUpdateTranslations
    ]
  );

  const onExit = useCallback(
    async (options?: OnExitOptions) => {
      if (exitInProgressRef.current) return;

      exitInProgressRef.current = true;

      let shouldResetExitInProgress = true;

      try {
        if (shouldCheckTranslationsBeforeExit) {
          if (hasTranslationGaps) {
            setShouldAutoUpdateTranslations(false);
            setDisplayUpdateTranslationsModal(true);
            return;
          }

          if (!apiService || training?.id == null) {
            onError();
            return;
          }

          const latestCourse = await apiService.getCourseInfo(training.id);
          if (latestCourse?.error || !latestCourse?.training) {
            onError();
            return;
          }

          const latestHasTranslationGaps = hasTrainingTranslationGaps(
            latestCourse.training
          );

          if (latestHasTranslationGaps) {
            setTraining(latestCourse.training);
            setShouldAutoUpdateTranslations(false);
            setDisplayUpdateTranslationsModal(true);
            return;
          }
        }

        // Close (X): skip publish-on-exit when live content is unchanged so we
        // don't flash "Saving..." with nothing to save (CAT-677).
        // Assign: always publish — it must use publishRedirectUrl / API redirect.
        // If timestamps are missing, keep the legacy publish-on-exit behavior.
        if (training?.status === 'live') {
          const editorTraining = training as EditorTraining;
          const hasKnownContentTimestamps =
            normalizeContentTimestamp(editorTraining.contentEditedAt) != null ||
            normalizeContentTimestamp(editorTraining.lastPublishedAt) != null;
          const shouldSkipPublish =
            !options?.requirePublish &&
            hasKnownContentTimestamps &&
            isTrainingContentInSync(editorTraining);

          if (!shouldSkipPublish) {
            setIsExitInProgress(true);
            const didNavigate = await doPublish(false);
            if (!didNavigate) {
              onError();
              return;
            }

            shouldResetExitInProgress = false;
            return;
          }
        }

        if (!window || !exitRedirectUrl) {
          return;
        }

        const resultRedirectUrl =
          process.env.REACT_APP_BACKEND_DASHBOARD_BASE_URL + exitRedirectUrl;

        if (window.parent && window.self !== window.parent) {
          window.parent.location.href = resultRedirectUrl;
        } else {
          window.location.href = resultRedirectUrl;
        }

        shouldResetExitInProgress = false;
      } catch {
        onError();
      } finally {
        if (shouldResetExitInProgress) {
          exitInProgressRef.current = false;
          setIsExitInProgress(false);
        }
      }
    },
    [
      apiService,
      doPublish,
      exitRedirectUrl,
      hasTranslationGaps,
      onError,
      setDisplayUpdateTranslationsModal,
      setShouldAutoUpdateTranslations,
      setTraining,
      shouldCheckTranslationsBeforeExit,
      training
    ]
  );

  return {
    isExitInProgress,
    publish,
    onExit
  };
};
